import React, { useState } from 'react';
import { Download, ImagePlay } from 'lucide-react';
import { TaskyAlert, TaskyButton, useConfirm } from '../tasky';
import adminVideoStyleRecipeService, {
  type AdminVideoStyleRecipe,
  type VideoStyleTrialDetail,
  videoStyleErrorMessages,
} from '../../services/api/adminVideoStyleRecipeService';

interface Props {
  trial: VideoStyleTrialDetail;
  /** 渡すと完成した試作に「このスタイルの見本にする」を出す */
  recipeId?: number | null;
  onSampleStarted?: (recipe: AdminVideoStyleRecipe) => void;
}

/**
 * 試作1本の結果: 失敗理由、完成動画のプレーヤーとダウンロード（スタイル詳細と「試作」タブで共通）。
 * 完成した試作はスタイル選択の見本にできる（全ユーザーに出るので確認を挟む）。
 */
const VideoStyleTrialPlayer: React.FC<Props> = ({ trial, recipeId, onSampleStarted }) => {
  const confirm = useConfirm();
  const [sampleMessage, setSampleMessage] = useState<{ tone: 'success' | 'error'; lines: string[] } | null>(null);
  const [isSetting, setIsSetting] = useState(false);
  const canMakeSample = recipeId != null && trial.status === 'completed' && Boolean(trial.video_url);

  const makeSample = async () => {
    if (recipeId == null) return;
    const ok = await confirm({
      title: 'このスタイルの見本にしますか？',
      description: 'この動画から作る静止画と短い動画（音なし）が、スタイル選択でこのスタイルの見本として全ユーザーに表示されます。お客さまの動画は選ばないでください。',
      confirmText: '見本にする',
      tone: 'warning',
    });
    if (!ok) return;
    setIsSetting(true);
    setSampleMessage(null);
    try {
      const recipe = await adminVideoStyleRecipeService.setSample(recipeId, trial.id);
      setSampleMessage({ tone: 'success', lines: ['見本を作っています。スタイルの詳細で出来上がりを確かめられます。'] });
      onSampleStarted?.(recipe);
    } catch (error) {
      setSampleMessage({ tone: 'error', lines: videoStyleErrorMessages(error, '見本にできませんでした。') });
    } finally {
      setIsSetting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-tasky-sm border border-warm-border p-2" data-testid="video-style-trial-player">
      {trial.status === 'failed' && trial.error && <TaskyAlert tone="error">{trial.error}</TaskyAlert>}
      {trial.video_url && <video src={trial.video_url} controls playsInline className="mx-auto max-h-[60vh] rounded bg-black" aria-label={`${trial.title}の試作動画`} />}
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
        <span className="truncate">
          {trial.title}
          {trial.appeal ? `（訴求: ${trial.appeal}）` : ''}
          {trial.output_seconds ? `・${trial.output_seconds.toFixed(1)}秒` : ''}
        </span>
        <span className="flex shrink-0 items-center gap-3">
          {canMakeSample && (
            <TaskyButton size="sm" variant="outline" onClick={() => void makeSample()} disabled={isSetting}>
              <ImagePlay aria-hidden />
              このスタイルの見本にする
            </TaskyButton>
          )}
          {trial.download_url && (
            <a href={trial.download_url} className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary hover:underline">
              <Download className="h-4 w-4" aria-hidden />
              ダウンロード
            </a>
          )}
        </span>
      </div>
      {sampleMessage && (
        <TaskyAlert tone={sampleMessage.tone}>
          {sampleMessage.lines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </TaskyAlert>
      )}
    </div>
  );
};

export default VideoStyleTrialPlayer;
